import React from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { PricingPlan, PricingAddon } from '../services/pricing.service';

interface Props {
  basePlans: PricingPlan[];
  variantsForSelected: PricingPlan[];
  selectedPlanCode: string | null;
  selectedVariantCode: string | null;
  addons: PricingAddon[];
  selectedAddonCodes: string[];
  isLoadingPlans: boolean;
  isLoadingAddons: boolean;
  onSelectPlan: (code: string) => void;
  onSelectVariant: (code: string) => void;
  onToggleAddon: (code: string) => void;
}

const fmt = (minor?: number) =>
  minor !== undefined
    ? `$${(minor / 100).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} MXN`
    : '—';

const MarketplacePricingSelector: React.FC<Props> = ({
  basePlans,
  variantsForSelected,
  selectedPlanCode,
  selectedVariantCode,
  addons,
  selectedAddonCodes,
  isLoadingPlans,
  isLoadingAddons,
  onSelectPlan,
  onSelectVariant,
  onToggleAddon,
}) => {
  if (isLoadingPlans) {
    return (
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-4">
        <LoadingOutlined className="animate-spin" />
        <span className="text-sm">Cargando planes...</span>
      </div>
    );
  }

  if (basePlans.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No hay planes disponibles.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {/* Plan selector */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Plan de facturación *
        </h4>
        <div className="grid grid-cols-1 gap-3">
          {basePlans.map((plan) => {
            const isSelected = plan.planCode === selectedPlanCode;
            const res = plan.includedResources;
            const inst = plan.providerConfiguration?.instanceType;
            return (
              <button
                key={plan.planCode}
                type="button"
                onClick={() => onSelectPlan(plan.planCode)}
                className={`w-full text-left px-4 py-3 border-2 rounded-lg transition-all ${
                  isSelected
                    ? 'border-black dark:border-white bg-gray-50 dark:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                        isSelected
                          ? 'border-black dark:border-white bg-black dark:bg-white'
                          : 'border-gray-300 dark:border-gray-500'
                      }`}
                    />
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {plan.displayName ?? plan.planName}
                      </p>
                      {inst && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {inst}
                          {res?.vcpu && ` • ${res.vcpu} vCPU`}
                          {res?.memoryMb && ` • ${res.memoryMb >= 1024 ? `${res.memoryMb / 1024} GB` : `${res.memoryMb} MB`} RAM`}
                        </p>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap ml-4">
                    desde {fmt(plan.priceAmountMinor)}/mes
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Variant selector */}
      {variantsForSelected.length > 1 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Variante (almacenamiento)
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {variantsForSelected.map((v) => {
              const isSelected = v.variantCode === selectedVariantCode;
              const res = v.includedResources;
              return (
                <button
                  key={v.variantCode}
                  type="button"
                  onClick={() => onSelectVariant(v.variantCode!)}
                  className={`p-3 border-2 rounded-lg text-left transition-all ${
                    isSelected
                      ? 'border-black dark:border-white bg-gray-50 dark:bg-gray-800'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {v.variantName ?? v.variantCode}
                  </p>
                  {res?.storageGb && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {res.storageGb} GB
                      {res.transferGb && ` • ${res.transferGb} GB transfer`}
                    </p>
                  )}
                  <p className="text-sm font-bold text-gray-900 dark:text-white mt-1">
                    {fmt(v.priceAmountMinor)}/mes
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Add-ons */}
      {!isLoadingAddons && addons.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Add-ons opcionales
          </h4>
          <div className="space-y-2">
            {addons.map((addon) => (
              <label
                key={addon.addonCode}
                className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedAddonCodes.includes(addon.addonCode)}
                    onChange={() => onToggleAddon(addon.addonCode)}
                    className="w-4 h-4 accent-black dark:accent-white"
                  />
                  <span className="text-sm text-gray-900 dark:text-white">{addon.addonName}</span>
                </div>
                <span className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap ml-4">
                  +{fmt(addon.priceAmountMinor)}/mes
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplacePricingSelector;
